import type { AdminContestDefinition } from "@repo/marketplace-admin"
import { Loader2, Save } from "lucide-react"
import * as React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { cn } from "@/utils/styles.helpers"

export interface ContestDatesFormValues {
	/** ISO 8601, UTC. */
	startsAt: string
	/** ISO 8601, UTC. */
	endsAt: string
}

export interface ContestCreateFormValues extends ContestDatesFormValues {
	id: string
	definitionId: string
}

/** Present when the form creates a contest: the id is typed and the rules picked. */
export interface ContestCreateOptions {
	definitions: AdminContestDefinition[]
	defaultId: string
}

interface ContestDatesFormProps {
	contestId: string
	initialValues?: ContestDatesFormValues
	onSubmit: (values: ContestCreateFormValues) => void
	isSubmitting: boolean
	create?: ContestCreateOptions
	/** Locks every input, for settled contests or read-only operators. */
	disabled?: boolean
	/** Shown next to the submit button while `disabled`. */
	disabledReason?: string
	/** Locks the start only, for live or ended contests whose window already scored. */
	startLocked?: boolean
	submitLabel?: string
	className?: string
}

const ZONES = [
	{ label: "UTC", timeZone: "UTC" },
	{ label: "Paris", timeZone: "Europe/Paris" },
] as const

const CONTEST_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

function pad(value: number): string {
	return String(value).padStart(2, "0")
}

/** Renders an ISO instant in the browser's local time as a datetime-local value. */
export function toLocalInputValue(iso: string | null | undefined): string {
	if (!iso) return ""
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return ""
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Parses a datetime-local value, which the browser expresses in local time. */
export function fromLocalInputValue(value: string): Date | null {
	if (!value) return null
	const date = new Date(value)
	return Number.isNaN(date.getTime()) ? null : date
}

function formatInZone(date: Date, timeZone: string): string {
	return new Intl.DateTimeFormat("en-GB", {
		timeZone,
		year: "numeric",
		month: "short",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
		timeZoneName: "short",
	}).format(date)
}

function ZoneHints({ value }: { value: string }) {
	const date = fromLocalInputValue(value)
	if (!date) {
		return <p className="text-xs text-muted-foreground">Not set</p>
	}

	return (
		<div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
			{ZONES.map((zone) => (
				<span key={zone.timeZone}>
					<span className="font-medium">{zone.label}:</span>{" "}
					{formatInZone(date, zone.timeZone)}
				</span>
			))}
		</div>
	)
}

function localTimeZone(): string {
	return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * A datetime-local input typed in the browser's local time zone, which is
 * named next to the input; the UTC and Paris renderings of the value follow
 * below it.
 */
export function DateTimeField({
	id,
	label,
	value,
	onChange,
	disabled,
	required,
}: {
	id: string
	label: string
	value: string
	onChange: (value: string) => void
	disabled?: boolean
	required?: boolean
}) {
	const hintId = `${id}-time-zone`

	return (
		<div className="space-y-1.5">
			<Label htmlFor={id}>
				{label}
				{required ? <span className="text-destructive"> *</span> : null}
			</Label>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
				<Input
					id={id}
					type="datetime-local"
					value={value}
					onChange={(event) => onChange(event.target.value)}
					disabled={disabled}
					required={required}
					aria-describedby={hintId}
					className="max-w-xs"
				/>
				<span id={hintId} className="text-xs text-muted-foreground">
					Your local time · {localTimeZone()}
				</span>
			</div>
			<ZoneHints value={value} />
		</div>
	)
}

function validate(values: {
	id: string
	startsAt: string
	endsAt: string
	creating: boolean
}): string | null {
	if (values.creating && !CONTEST_ID_PATTERN.test(values.id)) {
		return "The contest id is lowercase words separated by dashes, e.g. oct-2026-referral."
	}
	const startsAt = fromLocalInputValue(values.startsAt)
	const endsAt = fromLocalInputValue(values.endsAt)

	if (!startsAt) return "The start date is required."
	if (!endsAt) return "The end date is required."
	if (endsAt <= startsAt) return "The end date must come after the start date."
	return null
}

/**
 * Edits the two instants of a contest, and on creation its id and the
 * registered rules it runs on. Inputs are typed in the browser's local
 * time; the UTC and Paris renderings below each field disambiguate them.
 */
export function ContestDatesForm({
	contestId,
	initialValues,
	onSubmit,
	isSubmitting,
	create,
	disabled = false,
	disabledReason = "Dates are locked once the contest is settled.",
	startLocked = false,
	submitLabel = "Save dates",
	className,
}: ContestDatesFormProps) {
	const [id, setId] = React.useState(create?.defaultId ?? contestId)
	const [definitionId, setDefinitionId] = React.useState(
		create?.definitions[0]?.id ?? contestId,
	)
	const [startsAt, setStartsAt] = React.useState(() =>
		toLocalInputValue(initialValues?.startsAt),
	)
	const [endsAt, setEndsAt] = React.useState(() =>
		toLocalInputValue(initialValues?.endsAt),
	)
	const [validationError, setValidationError] = React.useState<string | null>(
		null,
	)

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		const error = validate({ id, startsAt, endsAt, creating: !!create })
		setValidationError(error)
		if (error) return

		const starts = fromLocalInputValue(startsAt)
		const ends = fromLocalInputValue(endsAt)
		if (!starts || !ends) return

		// The picker drops seconds, so a field left untouched resubmits the
		// stored instant rather than a truncated copy of it.
		const keepIfUntouched = (value: string, initial: string | undefined) =>
			initial !== undefined && value === toLocalInputValue(initial)
				? initial
				: null
		onSubmit({
			id,
			definitionId,
			startsAt:
				keepIfUntouched(startsAt, initialValues?.startsAt) ??
				starts.toISOString(),
			endsAt:
				keepIfUntouched(endsAt, initialValues?.endsAt) ?? ends.toISOString(),
		})
	}

	const isLocked = disabled || isSubmitting

	return (
		<form onSubmit={handleSubmit} className={cn("space-y-5", className)}>
			<div className="space-y-1.5">
				<Label htmlFor={`contest-id-${contestId}`}>
					Contest id
					{create ? <span className="text-destructive"> *</span> : null}
				</Label>
				<Input
					id={`contest-id-${contestId}`}
					value={id}
					onChange={(event) => setId(event.target.value.trim())}
					readOnly={!create}
					disabled={isLocked}
					className="max-w-xs font-mono"
				/>
				{create ? (
					<p className="text-xs text-muted-foreground">
						Lowercase words separated by dashes; shown in URLs, logs and Braze
						events.
					</p>
				) : null}
			</div>

			{create ? (
				<div className="space-y-1.5">
					<Label htmlFor={`contest-definition-${contestId}`}>Rules</Label>
					<Select
						value={definitionId}
						onValueChange={setDefinitionId}
						disabled={isLocked}
					>
						<SelectTrigger
							id={`contest-definition-${contestId}`}
							className="max-w-xs"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{create.definitions.map((definition) => (
								<SelectItem key={definition.id} value={definition.id}>
									{definition.id}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<p className="text-xs text-muted-foreground">
						Points, multipliers, tiers and rewards come from this registered
						rule set and cannot change once the contest exists.
					</p>
				</div>
			) : null}

			<DateTimeField
				id={`contest-starts-${contestId}`}
				label="Starts at"
				value={startsAt}
				onChange={setStartsAt}
				disabled={isLocked || startLocked}
				required
			/>
			<DateTimeField
				id={`contest-ends-${contestId}`}
				label="Ends at"
				value={endsAt}
				onChange={setEndsAt}
				disabled={isLocked}
				required
			/>

			{validationError ? (
				<p role="alert" className="text-sm text-destructive">
					{validationError}
				</p>
			) : null}

			<div className="flex items-center gap-3">
				<Button type="submit" disabled={isLocked}>
					{isSubmitting ? (
						<Loader2 className="h-4 w-4 mr-2 animate-spin" />
					) : (
						<Save className="h-4 w-4 mr-2" />
					)}
					{submitLabel}
				</Button>
				{disabled ? (
					<p className="text-sm text-muted-foreground">{disabledReason}</p>
				) : startLocked ? (
					<p className="text-sm text-muted-foreground">
						The start cannot move once the contest is live; the end can.
					</p>
				) : null}
			</div>
		</form>
	)
}
